import { useEffect, useState } from 'react';
import { createApiGateway } from '../apiGateway';
import { notification } from 'antd';

const apiGateway = createApiGateway();

type Dsp = {
  id: number;
  name: string;
};

type User = {
  id: number;
  firstName?: string;
};

type DspWithEnrichment = Dsp & {
  partnerId?: number;
  company: User | null;
  lastHourStatistics?: {
    dspBidRequest: number;
    dspBidResponseValid: number;
  };
  dailySpend?: number;
  totalSpend?: number;
};

export function useDsps() {
  const [dsps, setDsps] = useState<DspWithEnrichment[]>([]);
  const [isBaseLoaded, setIsBaseLoaded] = useState(false);
  const [isLastHourStatsLoaded, setIsLastHourStatsLoaded] = useState(false);
  const [isSpendLoaded, setIsSpendLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [dspsResponse, usersResponse] = await Promise.all([
          apiGateway.dsps.getAll({ includeSpend: false }),
          apiGateway.users.getAll(),
        ]);

        if (!isMounted) {
          return;
        }

        const dspsToSet = dspsResponse.map((dsp: DspWithEnrichment) => {
          const company = usersResponse.find((u: User) => u.id === dsp.partnerId) || null;

          return {
            ...dsp,
            company,
          };
        });

        setDsps(dspsToSet);
        setIsBaseLoaded(true);
      } catch (error) {
        if (isMounted) {
          setIsBaseLoaded(true);
        }
      }

      try {
        const lastHourStatisticsResponse = await apiGateway.statisticsV2.getLastHourDspStatistics();

        if (!isMounted) {
          return;
        }

        if (lastHourStatisticsResponse.rows.length) {
          notification.info({
            title: `Statistics loaded for ${lastHourStatisticsResponse.hour} UTC`,
            duration: 15,
          });
        } else {
          notification.info({
            title: 'No DSP statistics were found for the recent hourly window',
            duration: 10,
          });
        }

        setDsps((prevDsps) =>
          prevDsps.map((dsp) => ({
            ...dsp,
            lastHourStatistics: lastHourStatisticsResponse.rows.find((x) => String(x.dspId).includes(`[${dsp.id}]`)),
          }))
        );
      } catch (error) {
        // Ignore statistics errors and keep base list available.
      } finally {
        if (isMounted) {
          setIsLastHourStatsLoaded(true);
        }
      }

      try {
        const dspsWithSpend: Array<{ id: number; dailySpend?: number; totalSpend?: number }> =
          await apiGateway.dsps.getAll({ includeSpend: true });

        if (!isMounted) {
          return;
        }

        const spendById = new Map(
          dspsWithSpend.map((dsp) => [dsp.id, { dailySpend: dsp.dailySpend, totalSpend: dsp.totalSpend }])
        );

        setDsps((prevDsps) =>
          prevDsps.map((dsp) => ({
            ...dsp,
            dailySpend: spendById.get(dsp.id)?.dailySpend ?? 0,
            totalSpend: spendById.get(dsp.id)?.totalSpend ?? 0,
          }))
        );
      } catch (error) {
        // Ignore spend errors and keep base list available.
      } finally {
        if (isMounted) {
          setIsSpendLoaded(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return { dsps, isBaseLoaded, isLastHourStatsLoaded, isSpendLoaded };
}
